import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { refreshAfterToolWatchClear } from '@/hooks/useContacts';
import { contactsApi } from '@/lib/api/contacts.api';
import { messagingApi } from '@/lib/api/messaging.api';
import { useToolWatchStore } from '@/stores/tool-watch.store';

import type { ConversationQuery, StartConversationDto } from '@/lib/api/messaging.api';

const CONVERSATIONS_KEY = ['messaging-conversations'];
const MESSAGES_KEY = ['messaging-messages'];
const CONVERSATIONS_PAGE_SIZE = 20;
const MESSAGES_PAGE_SIZE = 50;

export const useConversations = (query: ConversationQuery = {}) =>
  useInfiniteQuery({
    queryKey: [...CONVERSATIONS_KEY, query],
    queryFn: ({ pageParam = 1 }) =>
      messagingApi.getConversations({ ...query, page: pageParam, limit: CONVERSATIONS_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / CONVERSATIONS_PAGE_SIZE);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    refetchInterval: 10000,
  });

export const useConversation = (id: string | undefined) =>
  useQuery({
    queryKey: [...CONVERSATIONS_KEY, id],
    queryFn: () => messagingApi.getConversation(id!),
    enabled: !!id,
  });

export const useConversationMessages = (conversationId: string | undefined) =>
  useInfiniteQuery({
    queryKey: [...MESSAGES_KEY, conversationId],
    queryFn: ({ pageParam = 1 }) =>
      messagingApi.getMessages(conversationId!, {
        page: pageParam,
        limit: MESSAGES_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / MESSAGES_PAGE_SIZE);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    enabled: !!conversationId,
    refetchInterval: 5000,
  });

export const useSendMessage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, content }: { conversationId: string; content: string }) =>
      messagingApi.sendMessage(conversationId, content),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MESSAGES_KEY, variables.conversationId] });
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
};

export const useMarkAsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => messagingApi.markAsRead(conversationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
  });
};

export const useSendTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      templateName,
      language,
      components,
      renderedText,
    }: {
      conversationId: string;
      templateName: string;
      language?: string;
      components?: Array<Record<string, unknown>>;
      renderedText?: string;
    }) =>
      messagingApi.sendTemplate(conversationId, templateName, language, components, renderedText),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MESSAGES_KEY, variables.conversationId] });
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
};

export const useSendImage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, file }: { conversationId: string; file: File }) =>
      messagingApi.sendImage(conversationId, file),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MESSAGES_KEY, variables.conversationId] });
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
};

export const useTakeoverConversation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => messagingApi.takeoverConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      qc.invalidateQueries({ queryKey: [...CONVERSATIONS_KEY, conversationId] });
      qc.invalidateQueries({ queryKey: [...MESSAGES_KEY, conversationId] });
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
};

export const useHandbackConversation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => messagingApi.handbackConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      qc.invalidateQueries({ queryKey: [...CONVERSATIONS_KEY, conversationId] });
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
};

export const useSetAutoHandback = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, minutes }: { conversationId: string; minutes: number | null }) =>
      messagingApi.setAutoHandback(conversationId, minutes),
    onSuccess: (_data, { conversationId }) => {
      qc.invalidateQueries({ queryKey: [...CONVERSATIONS_KEY, conversationId] });
    },
  });
};

export const useClaimConversation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId }: { conversationId: string; contactId?: string }) =>
      messagingApi.claimConversation(conversationId),
    onSuccess: (_data, { conversationId, contactId }) => {
      qc.invalidateQueries({ queryKey: [...CONVERSATIONS_KEY, conversationId] });
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      // Claiming is what stops an unclaimed contact's tool-watch flash.
      if (contactId) {
        const { flashes, clearFlash } = useToolWatchStore.getState();
        if (flashes[contactId]) {
          clearFlash(contactId);
          contactsApi
            .acknowledgeContactToolWatch(contactId)
            .then(() => refreshAfterToolWatchClear(qc, contactId))
            .catch(() => {});
        }
      }
    },
  });
};

export const useUnclaimConversation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => messagingApi.unclaimConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      qc.invalidateQueries({ queryKey: [...CONVERSATIONS_KEY, conversationId] });
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
};

export const useDeleteConversation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => messagingApi.deleteConversation(conversationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
  });
};

export const useUpdateConversationStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      status,
    }: {
      conversationId: string;
      status: 'OPEN' | 'CLOSED' | 'ARCHIVED';
    }) => messagingApi.updateStatus(conversationId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
  });
};

export const useStartConversation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: StartConversationDto) => messagingApi.startConversation(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
  });
};
